/**
 * DuplicatesDialog — the "Find & clean duplicate bills" tool (Sprint 2026-09).
 *
 * Trigger: a button on LibraryPage. On open, calls /api/duplicates to
 * fetch groups of bills that look like the same physical invoice
 * (matched by file hash, invoice #, or vendor + date + total).
 *
 * UX
 * ---
 * ┌─ Duplicate bills detected ──────────────────────────────────┐
 * │  We found 4 duplicate groups covering 11 bills.             │
 * │                                                             │
 * │  ▸ Shiv Gauri — INV 2627/TS002536 · Jun 15, 2026 · ₹7,543   │
 * │      Reason: same vendor, date and amount                   │
 * │      [KEEP] 🖼️ 2026-06-15 · uploaded Jul 01 (oldest)         │
 * │      [DEL]  🖼️ 2026-06-15 · uploaded Jul 03                 │
 * │      [DEL]  🖼️ 2026-06-15 · uploaded Jul 03                 │
 * │                                                             │
 * │  ▸ ...                                                      │
 * │                                                             │
 * │  10 bills marked for deletion (3.4 MB) [Delete selected] ▶  │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Rules for the default selection
 * -------------------------------
 * • Bills come from the backend already sorted oldest → newest by
 *   uploaded_at within each group. The OLDEST bill is defaulted to
 *   "Keep" (radio), the rest are checked for deletion.
 * • The user can switch which bill to keep by clicking a different
 *   "Keep" radio. Only one "Keep" per group; the rest are marked
 *   for deletion. If they uncheck a delete box, that copy survives too
 *   (so the group can leave with more than one bill if that's what the
 *   user wants).
 */

import { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertTriangle,
    ShieldCheck,
    Trash2,
    Loader2,
    FileText,
    CheckCircle2,
    Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, fmtMoney, fmtDate } from '../lib/api';

const REASON_META = {
    'same file uploaded more than once': {
        badge: 'Exact file match',
        tone: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/30 dark:text-red-100',
    },
    'same vendor and invoice number': {
        badge: 'Same invoice #',
        tone: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100',
    },
    'same vendor, date and amount': {
        badge: 'Same date + amount',
        tone: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100',
    },
};

export function DuplicatesDialog({ open, onOpenChange, onCleaned }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    // selection[groupKey][billId] = 'keep' | 'delete'
    const [selection, setSelection] = useState({});
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.findDuplicates();
            setData(res);
            // Seed default selection: first bill in each group is "keep", rest are "delete"
            const sel = {};
            for (const g of (res.groups || [])) {
                sel[g.key] = {};
                (g.bills || []).forEach((b, idx) => {
                    sel[g.key][b.id] = idx === 0 ? 'keep' : 'delete';
                });
            }
            setSelection(sel);
        } catch (e) {
            toast.error('Failed to scan for duplicates', {
                description: e?.response?.data?.detail || String(e),
            });
            setData({ groups: [], count: 0, bills_affected: 0 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    function setBillMode(groupKey, billId, mode) {
        setSelection((prev) => {
            const next = { ...prev, [groupKey]: { ...(prev[groupKey] || {}) } };
            if (mode === 'keep') {
                // Only one "keep" per group — everything else becomes "delete"
                for (const k of Object.keys(next[groupKey])) {
                    next[groupKey][k] = k === billId ? 'keep' : 'delete';
                }
            } else if (mode === 'delete') {
                next[groupKey][billId] = 'delete';
            } else {
                // 'skip' — leave this bill alone
                next[groupKey][billId] = 'skip';
            }
            return next;
        });
    }

    // Flatten the selection into an array of IDs to delete.
    const toDelete = [];
    if (data?.groups) {
        for (const g of data.groups) {
            for (const b of (g.bills || [])) {
                if (selection[g.key]?.[b.id] === 'delete') toDelete.push(b.id);
            }
        }
    }

    async function handleDelete() {
        if (toDelete.length === 0) {
            toast.info('Nothing selected for deletion.');
            return;
        }
        if (!window.confirm(
            `Delete ${toDelete.length} duplicate bill${toDelete.length === 1 ? '' : 's'}? ` +
            'This cannot be undone.',
        )) {
            return;
        }
        try {
            setBusy(true);
            const res = await api.bulkDeleteBills(toDelete);
            const n = (res.deleted || []).length;
            const errs = (res.errors || []).length;
            if (errs > 0) {
                toast.warning(`Deleted ${n} bills; ${errs} could not be removed`, {
                    description: 'The remaining bills stayed in your workspace.',
                });
            } else {
                toast.success(`Deleted ${n} duplicate bill${n === 1 ? '' : 's'}`, {
                    description: 'Your workspace is cleaner now.',
                });
            }
            onCleaned?.();
            // Refresh the dialog contents; if nothing left, close.
            const refreshed = await api.findDuplicates();
            setData(refreshed);
            if (!refreshed.groups?.length) {
                onOpenChange?.(false);
            }
        } catch (e) {
            toast.error('Delete failed', {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setBusy(false);
        }
    }

    const groups = data?.groups || [];
    const hasAny = groups.length > 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="duplicates-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle size={18} className="text-amber-600" />
                        Find & remove duplicate bills
                    </DialogTitle>
                    <DialogDescription>
                        We look for bills that appear more than once (same file, same invoice #, or same vendor + date + amount) and let you keep one copy from each group.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="space-y-3 py-4" data-testid="duplicates-loading">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="space-y-2">
                                <Skeleton className="h-4 w-1/2" />
                                <Skeleton className="h-14 w-full" />
                            </div>
                        ))}
                    </div>
                ) : !hasAny ? (
                    <div
                        className="flex flex-col items-center justify-center py-10 text-center"
                        data-testid="duplicates-empty"
                    >
                        <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                            <ShieldCheck size={30} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-semibold text-base mb-1">All clear!</h3>
                        <p className="text-sm text-muted-foreground max-w-md">
                            No duplicate bills found in your workspace. Every invoice looks unique.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="text-sm text-muted-foreground mb-2">
                            Found <span className="font-medium text-foreground">{groups.length}</span> duplicate group{groups.length === 1 ? '' : 's'} covering <span className="font-medium text-foreground">{data.bills_affected}</span> bills.
                        </div>
                        <ScrollArea className="flex-1 pr-3" data-testid="duplicates-scroll">
                            <div className="space-y-4">
                                {groups.map((g) => (
                                    <DuplicateGroupCard
                                        key={g.key}
                                        group={g}
                                        selection={selection[g.key] || {}}
                                        onSetMode={(bid, mode) => setBillMode(g.key, bid, mode)}
                                    />
                                ))}
                            </div>
                        </ScrollArea>
                    </>
                )}

                <DialogFooter className="mt-4 flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 text-xs text-muted-foreground">
                        {hasAny && (
                            <>
                                <span data-testid="duplicates-selected-count" className="font-medium text-foreground">
                                    {toDelete.length}
                                </span>{' '}
                                bill{toDelete.length === 1 ? '' : 's'} marked for deletion
                            </>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenChange?.(false)}
                        data-testid="duplicates-close-btn"
                    >
                        Close
                    </Button>
                    {hasAny && (
                        <Button
                            variant="destructive"
                            size="sm"
                            disabled={busy || toDelete.length === 0}
                            onClick={handleDelete}
                            data-testid="duplicates-delete-btn"
                        >
                            {busy ? (
                                <Loader2 size={14} className="animate-spin mr-1.5" />
                            ) : (
                                <Trash2 size={14} className="mr-1.5" />
                            )}
                            Delete {toDelete.length || ''} duplicate{toDelete.length === 1 ? '' : 's'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DuplicateGroupCard({ group, selection, onSetMode }) {
    const meta = REASON_META[group.reason] || REASON_META['same vendor, date and amount'];
    // Use the first bill's key details as the group label.
    const lead = group.bills[0] || {};
    return (
        <div
            className="rounded-lg border border-border bg-card p-3 shadow-sm"
            data-testid={`duplicate-group-${group.key}`}
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">
                        {lead.vendor || 'Unknown vendor'}
                        {lead.invoice_number && (
                            <span className="text-muted-foreground font-normal">
                                {' '}· #{lead.invoice_number}
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {fmtDate(lead.bill_date) || '—'} · {fmtMoney(lead.total)} · {group.size} copies
                    </div>
                </div>
                <Badge
                    variant="outline"
                    className={`text-[10px] uppercase tracking-wider ${meta.tone}`}
                >
                    {meta.badge}
                </Badge>
            </div>

            <div className="space-y-1.5">
                {group.bills.map((b, idx) => {
                    const mode = selection[b.id] || 'skip';
                    const isKeep = mode === 'keep';
                    const isDelete = mode === 'delete';
                    return (
                        <div
                            key={b.id}
                            className={
                                'flex items-center gap-2 sm:gap-3 rounded-md border px-2.5 py-1.5 text-xs ' +
                                (isKeep
                                    ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20'
                                    : isDelete
                                        ? 'border-red-300 bg-red-50/60 dark:bg-red-900/20 opacity-90'
                                        : 'border-border bg-background')
                            }
                            data-testid={`duplicate-bill-row-${b.id}`}
                        >
                            {/* Thumbnail */}
                            <div className="w-10 h-12 rounded overflow-hidden border border-border bg-muted shrink-0">
                                {b.thumbnail ? (
                                    <img src={b.thumbnail} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                        <FileText size={16} strokeWidth={1.4} />
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">
                                    Uploaded {fmtDate(b.uploaded_at) || '—'}
                                    {idx === 0 && (
                                        <span className="ml-1.5 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                                            oldest
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] text-muted-foreground truncate">
                                    ID: {b.id}
                                </div>
                            </div>

                            {/* Mode toggles */}
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => onSetMode(b.id, 'keep')}
                                    className={
                                        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium ' +
                                        (isKeep
                                            ? 'border-emerald-400 bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100'
                                            : 'border-border text-muted-foreground hover:bg-muted')
                                    }
                                    data-testid={`duplicate-keep-${b.id}`}
                                    aria-pressed={isKeep}
                                >
                                    <CheckCircle2 size={12} />
                                    Keep
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onSetMode(b.id, 'delete')}
                                    className={
                                        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium ' +
                                        (isDelete
                                            ? 'border-red-400 bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100'
                                            : 'border-border text-muted-foreground hover:bg-muted')
                                    }
                                    data-testid={`duplicate-delete-${b.id}`}
                                    aria-pressed={isDelete}
                                >
                                    <Trash2 size={12} />
                                    Delete
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                <Sparkles size={10} />
                Matched by: {group.reason}
            </div>
        </div>
    );
}
